import { CheckInput, Select, TextInput } from '@octano/global-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col } from 'reactstrap';
import { useMemo, useCallback, useEffect } from 'react';

import { useParameters } from '../../hooks/useParameters';
import { useValidations } from '../../hooks/useValidations';
import { IdentificationFormProps, IdentificationInputs } from './types';

const IdentificationFormFieldsV1 = ({
  identificationTypes = [],
  readOnly = false,
  disabled = false,
}: IdentificationFormProps) => {
  const prefix = 'loginTuitionProcess';
  const { t } = useTranslation();
  const { countryOptions } = useParameters();

  const {
    msgValidations,
    validateRut,
    validateTextNotEmpty,
  } = useValidations();

  const { control, watch, setValue } = useFormContext<IdentificationInputs>();

  const selectedIdentificationOption = watch('identificationType');

  const defaultIdentification = useMemo(
    () =>
      identificationTypes?.find((e) => e.id === 'CL_RUT') ||
      identificationTypes?.find((e) => e.id === 'PASSPORT') ||
      identificationTypes[0],
    [identificationTypes],
  );

  const passportIdentification = useMemo(
    () => identificationTypes?.find((e) => e.id === 'PASSPORT'),
    [identificationTypes],
  );

  const selectedIdentification = useMemo(
    () =>
      identificationTypes?.find(
        (e) => e.id === selectedIdentificationOption?.value,
      ),
    [identificationTypes, selectedIdentificationOption?.value],
  );

  const handleChangePassportCheck = useCallback(() => {
    if (
      !passportIdentification ||
      !defaultIdentification ||
      defaultIdentification?.id === 'PASSPORT' ||
      readOnly ||
      disabled
    ) {
      return;
    }
    setValue(
      'identificationType',
      selectedIdentification?.id === 'PASSPORT'
        ? { value: defaultIdentification.id, label: defaultIdentification.name }
        : {
            value: passportIdentification.id,
            label: passportIdentification.name,
          },
    );
  }, [
    passportIdentification,
    defaultIdentification,
    setValue,
    readOnly,
    disabled,
    selectedIdentification?.id,
  ]);

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined = undefined;
    if (
      defaultIdentification &&
      identificationTypes?.length &&
      !selectedIdentification
    ) {
      timerId = setTimeout(() => {
        setValue('identificationType', {
          value: defaultIdentification.id,
          label: defaultIdentification.name,
        });
      }, 500);
    }

    return () => {
      if (timerId !== undefined) {
        clearTimeout(timerId);
      }
    };
  }, [
    defaultIdentification,
    identificationTypes,
    setValue,
    selectedIdentification,
  ]);

  if (!selectedIdentification) {
    return <></>;
  }

  return (
    <>
      {!!(
        selectedIdentification?.id && selectedIdentification?.id !== 'PASSPORT'
      ) && (
        <Col xs={12}>
          {!!defaultIdentification && (
            <TextInput
              name="identificationValue"
              label={selectedIdentification.name}
              control={control}
              formatter={
                selectedIdentification?.id === 'CL_RUT' ? 'rut' : undefined
              }
              rules={{
                validate:
                  selectedIdentification?.id === 'CL_RUT'
                    ? {
                        empty: validateTextNotEmpty,
                        rut: validateRut,
                      }
                    : { empty: validateTextNotEmpty },
              }}
              readOnly={readOnly}
              disabled={disabled}
              shouldUnregister={true}
            />
          )}
        </Col>
      )}
      {!!(
        selectedIdentification?.id === 'PASSPORT' ||
        (selectedIdentification?.id && !selectedIdentification?.countryId)
      ) && (
        <>
          <Col xs={12} md={6} className="pr-md-1">
            <Select
              name="identificationCountry"
              label={t(`loginTuitionProcess.identificationCountry`)}
              control={control}
              options={countryOptions}
              rules={{
                required: msgValidations.required,
              }}
              disabled={disabled || readOnly}
              shouldUnregister={true}
              isClearable={false}
            />
          </Col>
          <Col xs={12} md={6} className="pl-md-1">
            <TextInput
              name="identificationValue"
              label={selectedIdentification.name}
              rules={{
                validate: {
                  empty: validateTextNotEmpty,
                },
              }}
              readOnly={readOnly}
              disabled={disabled}
              control={control}
              shouldUnregister={true}
            />
          </Col>
        </>
      )}

      {!!(
        passportIdentification && defaultIdentification?.id !== 'PASSPORT'
      ) && (
        <Col xs="12">
          <CheckInput
            name="hasPassport"
            label={t(`${prefix}.labelCheckPassport`)}
            value={selectedIdentification?.id === 'PASSPORT'}
            readOnly={readOnly}
            disabled={disabled}
            onChange={handleChangePassportCheck}
          />
        </Col>
      )}
    </>
  );
};

export default IdentificationFormFieldsV1;
